import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, X, Search, Shield, CheckCircle2, Link as LinkIcon } from 'lucide-react';
import { certificationsService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function CertificationsPage() {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    icon: 'shield',
    button_text: 'Découvrir',
    button_link: '/contact',
    display_order: 0,
    is_active: true,
    items: ['']
  });

  useEffect(() => {
    loadCertifications();
  }, []);

  const loadCertifications = async () => {
    try {
      const response = await certificationsService.getAllAdmin();
      console.log('Certifications chargées:', response.data);
      setCertifications(response.data.certifications || []);
    } catch (error) {
      console.error('Erreur:', error);
      try {
        const response = await certificationsService.getAll();
        setCertifications(response.data.certifications || []);
      } catch (err) {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const openModal = (certification = null) => {
    if (certification) {
      setEditing(certification);
      setFormData({
        title: certification.title || '',
        subtitle: certification.subtitle || '',
        icon: certification.icon || 'shield',
        button_text: certification.button_text || 'Découvrir',
        button_link: certification.button_link || '/contact',
        display_order: certification.display_order || 0,
        is_active: certification.is_active ? true : false,
        items: certification.items && certification.items.length > 0 
          ? certification.items.map(item => item.label || item) 
          : ['']
      });
    } else {
      setEditing(null);
      setFormData({
        title: '',
        subtitle: '',
        icon: 'shield',
        button_text: 'Découvrir',
        button_link: '/contact',
        display_order: certifications.length + 1,
        is_active: true,
        items: ['']
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error('Le titre est requis');
      return;
    }

    // Filtrer les items vides
    const cleanedItems = formData.items.filter(item => item.trim() !== '');

    const dataToSend = {
      ...formData,
      items: cleanedItems.map(label => ({ label }))
    };

    try {
      if (editing) {
        await certificationsService.update(editing.id, dataToSend);
        toast.success('Certification mise à jour');
      } else {
        await certificationsService.create(dataToSend);
        toast.success('Certification créée');
      }
      setShowModal(false);
      loadCertifications();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette certification ?')) return;

    try {
      await certificationsService.delete(id);
      toast.success('Certification supprimée');
      loadCertifications();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (certification) => {
    try {
      await certificationsService.update(certification.id, { is_active: !certification.is_active });
      toast.success('Statut mis à jour');
      loadCertifications();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, '']
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? value : item)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Certifications</h1>
          <p className="text-dark-500">Gérez les cartes de certification affichées sur la page d'accueil</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Nouvelle certification
        </button>
      </div>

      {/* Liste */}
      {certifications.length === 0 ? (
        <div className="card p-12 text-center">
          <Shield className="w-16 h-16 text-dark-300 mx-auto mb-4" />
          <p className="text-dark-500 mb-4">Aucune certification</p>
          <button onClick={() => openModal()} className="btn-primary">
            Ajouter une certification
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card p-6 relative group"
            >
              {/* Actions */}
              <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openModal(cert)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-dark-50"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4 text-dark-600" />
                </button>
                <button
                  onClick={() => handleDelete(cert.id)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-red-50"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>

              {/* Contenu */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-dark-900 mb-1">{cert.title}</h3>
                <p className="text-dark-500 text-sm">{cert.subtitle}</p>
              </div>

              {/* Items */}
              <div className="space-y-2 mb-4">
                {cert.items && cert.items.map((item, i) => (
                  <div key={item.id || i} className="flex items-center gap-2 p-2 bg-dark-50 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                    <span className="text-sm text-dark-700">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Bouton info */}
              <div className="flex items-center gap-2 text-xs text-dark-400 mb-4">
                <LinkIcon className="w-3 h-3" />
                <span>{cert.button_link}</span>
              </div>

              {/* Status */}
              <div className="pt-4 border-t border-dark-100 flex items-center justify-between">
                <span className="text-xs text-dark-400">
                  Ordre: #{cert.display_order}
                </span>
                <button
                  onClick={() => toggleActive(cert)}
                  className={`badge cursor-pointer ${cert.is_active ? 'badge-secondary' : 'bg-dark-100 text-dark-500'}`}
                >
                  {cert.is_active ? 'Actif' : 'Inactif'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-dark-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">
                {editing ? 'Modifier la certification' : 'Nouvelle certification'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Titre */}
              <div>
                <label className="label">Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="input"
                  required
                  placeholder="Ex: Certification ISO 20022"
                />
              </div>

              {/* Sous-titre */}
              <div>
                <label className="label">Sous-titre</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                  className="input"
                  placeholder="Ex: Expertise reconnue sur le standard international"
                />
              </div>

              {/* Items */}
              <div>
                <label className="label">Points clés</label>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateItem(index, e.target.value)}
                        className="input flex-1"
                        placeholder={`Point ${index + 1}`}
                      />
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un point
                </button>
              </div>

              {/* Bouton */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Texte du bouton</label>
                  <input
                    type="text"
                    value={formData.button_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, button_text: e.target.value }))}
                    className="input"
                    placeholder="Découvrir"
                  />
                </div>
                <div>
                  <label className="label">Lien du bouton</label>
                  <input
                    type="text"
                    value={formData.button_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, button_link: e.target.value }))}
                    className="input"
                    placeholder="/contact"
                  />
                </div>
              </div>

              {/* Ordre d'affichage */}
              <div>
                <label className="label">Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="input"
                  min="0"
                />
              </div>

              {/* Actif */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-dark-700">
                  Certification active (visible sur le site)
                </label>
              </div>

              {/* Aperçu */}
              <div className="p-4 bg-dark-50 rounded-xl">
                <p className="text-xs text-dark-400 mb-3">Aperçu</p>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mx-auto mb-2">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-dark-900">{formData.title || 'Titre'}</h3>
                    <p className="text-dark-500 text-xs">{formData.subtitle || 'Sous-titre'}</p>
                  </div>
                  <div className="space-y-1">
                    {formData.items.filter(i => i.trim()).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="w-3 h-3 text-secondary-500" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default CertificationsPage;